import { createContext, useContext, useState, useEffect } from "react";

// Available languages
export const languages = {
  en: { name: "English", flag: "🇺🇸" },
  es: { name: "Español", flag: "🇪🇸" },
  fr: { name: "Français", flag: "🇫🇷" },
  de: { name: "Deutsch", flag: "🇩🇪" },
  pt: { name: "Português", flag: "🇧🇷" },
  zh: { name: "中文", flag: "🇨🇳" },
  ja: { name: "日本語", flag: "🇯🇵" },
  hi: { name: "हिन्दी", flag: "🇮🇳" }
};

// Translations
const translations = {
  en: {
    // Common
    welcome: "Welcome",
    dashboard: "Dashboard",
    profile: "Profile",
    messages: "Messages",
    settings: "Settings",
    logout: "Logout",
    save: "Save",
    cancel: "Cancel",
    search: "Search",
    filter: "Filter",
    apply: "Apply",
    loading: "Loading...",
    
    // Navigation
    browseJobs: "Browse Jobs",
    externalJobs: "External Jobs",
    community: "Community",
    interviews: "Interviews",
    insights: "Insights",
    analytics: "Analytics",
    calendar: "Calendar",
    notifications: "Notifications",
    
    // Jobs
    postJob: "Post Job",
    findCandidates: "Find Candidates",
    compareCandidates: "Compare Candidates",
    jobTitle: "Job Title",
    company: "Company",
    location: "Location",
    salary: "Salary",
    applyNow: "Apply Now",
    applied: "Applied",
    matchScore: "Match Score",
    
    // Profile
    editProfile: "Edit Profile",
    skills: "Skills",
    experience: "Experience",
    education: "Education",
    resume: "Resume",
    linkedin: "LinkedIn",
    
    // Messages
    newMessage: "New Message",
    typeMessage: "Type a message...",
    send: "Send",
    
    // Auth
    signIn: "Sign In",
    signUp: "Sign Up",
    email: "Email",
    password: "Password",
    
    // Dashboard
    welcomeBack: "Welcome back",
    careerJourney: "Your career journey continues here",
    hiringPipeline: "Manage your hiring pipeline",
    applications: "Applications",
    activeJobs: "Active Jobs",
    totalApplicants: "Total Applicants",
    profileViews: "Profile Views",
    
    // Analytics
    advancedAnalytics: "Advanced Analytics",
    trends: "Trends",
    performance: "Performance",
    export: "Export",
    
    // Notifications
    notificationSettings: "Notification Settings",
    pushNotifications: "Push Notifications",
    emailNotifications: "Email Notifications",
    enableNotifications: "Enable notifications",
    
    // Additional features
    resumeOptimization: "Resume Optimization",
    skillGapAnalysis: "Skill Gap Analysis",
    attachments: "Attachments",
    uploadFile: "Upload File",
    sendMessage: "Send Message",
    shortlisted: "Shortlisted",
    interviewed: "Interviewed",
    offered: "Offered",
    hired: "Hired",
    rejected: "Rejected",
    pending: "Pending",
    reviewed: "Reviewed",
    applicantPipeline: "Applicant Pipeline",
    quickActions: "Quick Actions",
    viewProfile: "View Profile",
    inviteInterview: "Invite to Interview",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    supportCenter: "Support Center",
    faq: "FAQ",
  },
  es: {
    welcome: "Bienvenido",
    dashboard: "Panel",
    profile: "Perfil",
    messages: "Mensajes",
    settings: "Configuración",
    logout: "Cerrar sesión",
    save: "Guardar",
    cancel: "Cancelar",
    search: "Buscar",
    filter: "Filtrar",
    apply: "Aplicar",
    loading: "Cargando...",
    
    browseJobs: "Buscar Empleos",
    externalJobs: "Empleos Externos",
    community: "Comunidad",
    interviews: "Entrevistas",
    insights: "Perspectivas",
    analytics: "Analíticas",
    calendar: "Calendario",
    notifications: "Notificaciones",
    
    postJob: "Publicar Empleo",
    findCandidates: "Buscar Candidatos",
    compareCandidates: "Comparar Candidatos",
    jobTitle: "Título del Puesto",
    company: "Empresa",
    location: "Ubicación",
    salary: "Salario",
    applyNow: "Aplicar Ahora",
    applied: "Aplicado",
    matchScore: "Puntuación",
    
    editProfile: "Editar Perfil",
    skills: "Habilidades",
    experience: "Experiencia",
    education: "Educación",
    resume: "Currículum",
    linkedin: "LinkedIn",
    
    newMessage: "Nuevo Mensaje",
    typeMessage: "Escribe un mensaje...",
    send: "Enviar",
    
    signIn: "Iniciar Sesión",
    signUp: "Registrarse",
    email: "Correo",
    password: "Contraseña",
    
    welcomeBack: "Bienvenido de nuevo",
    careerJourney: "Tu viaje profesional continúa aquí",
    hiringPipeline: "Gestiona tu proceso de contratación",
    applications: "Solicitudes",
    activeJobs: "Empleos Activos",
    totalApplicants: "Total Candidatos",
    profileViews: "Vistas del Perfil",
    
    advancedAnalytics: "Analíticas Avanzadas",
    trends: "Tendencias",
    performance: "Rendimiento",
    export: "Exportar",
    
    notificationSettings: "Configuración de Notificaciones",
    pushNotifications: "Notificaciones Push",
    emailNotifications: "Notificaciones por Email",
    enableNotifications: "Activar notificaciones",
  },
  fr: {
    welcome: "Bienvenue",
    dashboard: "Tableau de bord",
    profile: "Profil",
    messages: "Messages",
    settings: "Paramètres",
    logout: "Déconnexion",
    save: "Sauvegarder",
    cancel: "Annuler",
    search: "Rechercher",
    filter: "Filtrer",
    apply: "Appliquer",
    loading: "Chargement...",
    
    browseJobs: "Parcourir les Emplois",
    externalJobs: "Emplois Externes",
    community: "Communauté",
    interviews: "Entretiens",
    insights: "Insights",
    analytics: "Analytique",
    calendar: "Calendrier",
    notifications: "Notifications",
    
    postJob: "Publier un Emploi",
    findCandidates: "Trouver des Candidats",
    compareCandidates: "Comparer les Candidats",
    jobTitle: "Titre du Poste",
    company: "Entreprise",
    location: "Lieu",
    salary: "Salaire",
    applyNow: "Postuler",
    applied: "Postulé",
    matchScore: "Score",
    
    editProfile: "Modifier le Profil",
    skills: "Compétences",
    experience: "Expérience",
    education: "Formation",
    resume: "CV",
    linkedin: "LinkedIn",
    
    newMessage: "Nouveau Message",
    typeMessage: "Tapez un message...",
    send: "Envoyer",
    
    signIn: "Se Connecter",
    signUp: "S'inscrire",
    email: "Email",
    password: "Mot de passe",
    
    welcomeBack: "Bon retour",
    careerJourney: "Votre parcours professionnel continue ici",
    hiringPipeline: "Gérez votre pipeline de recrutement",
    applications: "Candidatures",
    activeJobs: "Emplois Actifs",
    totalApplicants: "Total Candidats",
    profileViews: "Vues du Profil",
    
    advancedAnalytics: "Analytique Avancée",
    trends: "Tendances",
    performance: "Performance",
    export: "Exporter",
    
    notificationSettings: "Paramètres de Notification",
    pushNotifications: "Notifications Push",
    emailNotifications: "Notifications Email",
    enableNotifications: "Activer les notifications",
  },
  de: {
    welcome: "Willkommen",
    dashboard: "Dashboard",
    profile: "Profil",
    messages: "Nachrichten",
    settings: "Einstellungen",
    logout: "Abmelden",
    save: "Speichern",
    cancel: "Abbrechen",
    search: "Suchen",
    filter: "Filtern",
    apply: "Anwenden",
    loading: "Laden...",
    
    browseJobs: "Jobs Durchsuchen",
    externalJobs: "Externe Jobs",
    community: "Community",
    interviews: "Interviews",
    insights: "Einblicke",
    analytics: "Analytik",
    calendar: "Kalender",
    notifications: "Benachrichtigungen",
    
    postJob: "Job Veröffentlichen",
    findCandidates: "Kandidaten Finden",
    compareCandidates: "Kandidaten Vergleichen",
    jobTitle: "Jobtitel",
    company: "Unternehmen",
    location: "Standort",
    salary: "Gehalt",
    applyNow: "Jetzt Bewerben",
    applied: "Beworben",
    matchScore: "Match-Score",
    
    editProfile: "Profil Bearbeiten",
    skills: "Fähigkeiten",
    experience: "Erfahrung",
    education: "Ausbildung",
    resume: "Lebenslauf",
    linkedin: "LinkedIn",
    
    newMessage: "Neue Nachricht",
    typeMessage: "Nachricht eingeben...",
    send: "Senden",
    
    signIn: "Anmelden",
    signUp: "Registrieren",
    email: "E-Mail",
    password: "Passwort",
    
    welcomeBack: "Willkommen zurück",
    careerJourney: "Ihre Karrierereise geht hier weiter",
    hiringPipeline: "Verwalten Sie Ihre Recruiting-Pipeline",
    applications: "Bewerbungen",
    activeJobs: "Aktive Jobs",
    totalApplicants: "Gesamtbewerber",
    profileViews: "Profilaufrufe",
    
    advancedAnalytics: "Erweiterte Analytik",
    trends: "Trends",
    performance: "Leistung",
    export: "Exportieren",
    
    notificationSettings: "Benachrichtigungseinstellungen",
    pushNotifications: "Push-Benachrichtigungen",
    emailNotifications: "E-Mail-Benachrichtigungen",
    enableNotifications: "Benachrichtigungen aktivieren",
  },
  pt: {
    welcome: "Bem-vindo",
    dashboard: "Painel",
    profile: "Perfil",
    messages: "Mensagens",
    settings: "Configurações",
    logout: "Sair",
    save: "Salvar",
    cancel: "Cancelar",
    search: "Buscar",
    filter: "Filtrar",
    apply: "Aplicar",
    loading: "Carregando...",
    
    browseJobs: "Buscar Vagas",
    externalJobs: "Vagas Externas",
    community: "Comunidade",
    interviews: "Entrevistas",
    insights: "Insights",
    analytics: "Análises",
    calendar: "Calendário",
    notifications: "Notificações",
    
    postJob: "Publicar Vaga",
    findCandidates: "Encontrar Candidatos",
    compareCandidates: "Comparar Candidatos",
    jobTitle: "Título da Vaga",
    company: "Empresa",
    location: "Localização",
    salary: "Salário",
    applyNow: "Candidatar-se",
    applied: "Candidatado",
    matchScore: "Pontuação",
    
    editProfile: "Editar Perfil",
    skills: "Habilidades",
    experience: "Experiência",
    education: "Educação",
    resume: "Currículo",
    linkedin: "LinkedIn",
    
    newMessage: "Nova Mensagem",
    typeMessage: "Digite uma mensagem...",
    send: "Enviar",
    
    signIn: "Entrar",
    signUp: "Cadastrar",
    email: "E-mail",
    password: "Senha",
    
    welcomeBack: "Bem-vindo de volta",
    careerJourney: "Sua jornada profissional continua aqui",
    hiringPipeline: "Gerencie seu pipeline de contratação",
    applications: "Candidaturas",
    activeJobs: "Vagas Ativas",
    totalApplicants: "Total de Candidatos",
    profileViews: "Visualizações do Perfil",
    
    advancedAnalytics: "Análises Avançadas",
    trends: "Tendências",
    performance: "Desempenho",
    export: "Exportar",
    
    notificationSettings: "Configurações de Notificação",
    pushNotifications: "Notificações Push",
    emailNotifications: "Notificações por E-mail",
    enableNotifications: "Ativar notificações",
  },
  zh: {
    welcome: "欢迎",
    dashboard: "仪表板",
    profile: "个人资料",
    messages: "消息",
    settings: "设置",
    logout: "退出登录",
    save: "保存",
    cancel: "取消",
    search: "搜索",
    filter: "筛选",
    apply: "申请",
    loading: "加载中...",
    
    browseJobs: "浏览职位",
    externalJobs: "外部职位",
    community: "社区",
    interviews: "面试",
    insights: "洞察",
    analytics: "分析",
    calendar: "日历",
    notifications: "通知",
    
    postJob: "发布职位",
    findCandidates: "寻找候选人",
    compareCandidates: "比较候选人",
    jobTitle: "职位名称",
    company: "公司",
    location: "地点",
    salary: "薪资",
    applyNow: "立即申请",
    applied: "已申请",
    matchScore: "匹配分数",
    
    editProfile: "编辑资料",
    skills: "技能",
    experience: "经验",
    education: "教育",
    resume: "简历",
    linkedin: "领英",
    
    newMessage: "新消息",
    typeMessage: "输入消息...",
    send: "发送",
    
    signIn: "登录",
    signUp: "注册",
    email: "邮箱",
    password: "密码",
    
    welcomeBack: "欢迎回来",
    careerJourney: "您的职业之旅在此继续",
    hiringPipeline: "管理您的招聘流程",
    applications: "申请",
    activeJobs: "活跃职位",
    totalApplicants: "申请人总数",
    profileViews: "资料浏览量",
    
    advancedAnalytics: "高级分析",
    trends: "趋势",
    performance: "绩效",
    export: "导出",
    
    notificationSettings: "通知设置",
    pushNotifications: "推送通知",
    emailNotifications: "邮件通知",
    enableNotifications: "启用通知",
  },
  ja: {
    welcome: "ようこそ",
    dashboard: "ダッシュボード",
    profile: "プロフィール",
    messages: "メッセージ",
    settings: "設定",
    logout: "ログアウト",
    save: "保存",
    cancel: "キャンセル",
    search: "検索",
    filter: "フィルター",
    apply: "応募",
    loading: "読み込み中...",
    
    browseJobs: "求人を探す",
    externalJobs: "外部求人",
    community: "コミュニティ",
    interviews: "面接",
    insights: "インサイト",
    analytics: "分析",
    calendar: "カレンダー",
    notifications: "通知",
    
    postJob: "求人を投稿",
    findCandidates: "候補者を探す",
    compareCandidates: "候補者を比較",
    jobTitle: "職種名",
    company: "会社",
    location: "勤務地",
    salary: "給与",
    applyNow: "今すぐ応募",
    applied: "応募済み",
    matchScore: "マッチスコア",
    
    editProfile: "プロフィール編集",
    skills: "スキル",
    experience: "経験",
    education: "学歴",
    resume: "履歴書",
    linkedin: "LinkedIn",
    
    newMessage: "新規メッセージ",
    typeMessage: "メッセージを入力...",
    send: "送信",
    
    signIn: "サインイン",
    signUp: "サインアップ",
    email: "メール",
    password: "パスワード",
    
    welcomeBack: "おかえりなさい",
    careerJourney: "キャリアの旅はここから続きます",
    hiringPipeline: "採用パイプラインを管理",
    applications: "応募",
    activeJobs: "アクティブな求人",
    totalApplicants: "応募者合計",
    profileViews: "プロフィール閲覧数",
    
    advancedAnalytics: "高度な分析",
    trends: "トレンド",
    performance: "パフォーマンス",
    export: "エクスポート",
    
    notificationSettings: "通知設定",
    pushNotifications: "プッシュ通知",
    emailNotifications: "メール通知",
    enableNotifications: "通知を有効にする",
  },
  hi: {
    welcome: "स्वागत है",
    dashboard: "डैशबोर्ड",
    profile: "प्रोफ़ाइल",
    messages: "संदेश",
    settings: "सेटिंग्स",
    logout: "लॉग आउट",
    save: "सेव करें",
    cancel: "रद्द करें",
    search: "खोजें",
    filter: "फ़िल्टर",
    apply: "आवेदन करें",
    loading: "लोड हो रहा है...",
    
    browseJobs: "नौकरियां ब्राउज़ करें",
    externalJobs: "बाहरी नौकरियां",
    community: "समुदाय",
    interviews: "साक्षात्कार",
    insights: "अंतर्दृष्टि",
    analytics: "विश्लेषण",
    calendar: "कैलेंडर",
    notifications: "सूचनाएं",
    
    postJob: "नौकरी पोस्ट करें",
    findCandidates: "उम्मीदवार खोजें",
    compareCandidates: "उम्मीदवारों की तुलना करें",
    jobTitle: "पद का नाम",
    company: "कंपनी",
    location: "स्थान",
    salary: "वेतन",
    applyNow: "अभी आवेदन करें",
    applied: "आवेदन किया",
    matchScore: "मैच स्कोर",
    
    editProfile: "प्रोफ़ाइल संपादित करें",
    skills: "कौशल",
    experience: "अनुभव",
    education: "शिक्षा",
    resume: "रिज्यूमे",
    linkedin: "लिंक्डइन",
    
    newMessage: "नया संदेश",
    typeMessage: "संदेश टाइप करें...",
    send: "भेजें",
    
    signIn: "साइन इन",
    signUp: "साइन अप",
    email: "ईमेल",
    password: "पासवर्ड",
    
    welcomeBack: "वापसी पर स्वागत है",
    careerJourney: "आपकी करियर यात्रा यहाँ जारी है",
    hiringPipeline: "अपनी भर्ती पाइपलाइन प्रबंधित करें",
    applications: "आवेदन",
    activeJobs: "सक्रिय नौकरियां",
    totalApplicants: "कुल आवेदक",
    profileViews: "प्रोफ़ाइल दृश्य",
    
    advancedAnalytics: "उन्नत विश्लेषण",
    trends: "रुझान",
    performance: "प्रदर्शन",
    export: "निर्यात",
    
    notificationSettings: "सूचना सेटिंग्स",
    pushNotifications: "पुश सूचनाएं",
    emailNotifications: "ईमेल सूचनाएं",
    enableNotifications: "सूचनाएं सक्षम करें",
  }
};

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  t: (key) => key,
});

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const stored = localStorage.getItem("rekrutor-language");
    if (stored && translations[stored]) return stored;
    // Try to detect browser language
    const browserLang = navigator.language?.split("-")[0];
    if (browserLang && translations[browserLang]) return browserLang;
    return "en";
  });

  useEffect(() => {
    localStorage.setItem("rekrutor-language", language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
    }
  };

  // Translation function
  const t = (key) => {
    return translations[language]?.[key] || translations.en?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
